import { StrokeBounds, type StrokeArrival, type StrokePoint, type StrokeRenderCounters } from '@questvisionstream/client';

/**
 * A live stroke as the renderer holds it: the wire message reduced to a
 * point list plus the frozen capture-frame arrival it is placed against.
 */
export interface LiveStroke {
  readonly id: string;
  readonly sender: string;
  readonly color: string;
  readonly width: number;
  /** Viewport-space points (0..1, y-up), grown by appends. */
  points: StrokePoint[];
  /** The frame arrival (ms, pose clock) the stroke is placed through; null until begin/end supplied one. */
  poseArrivalMs: number | null;
  /** True once `end` arrived - the stroke persists for the TTL from then. */
  committed: boolean;
  /** Pose-clock time the stroke expires (committed strokes only). */
  expiresAtMs: number | null;
  /** Last append seq, for loss detection. */
  seq: number;
  /** Marked when points changed since the last render. */
  dirty: boolean;
}

/**
 * The stroke lifecycle reducer (Plan-04 §3.2) - pure, so it is testable
 * without three.js. Enforces the shared bounds (max live strokes, TTL),
 * applies begin/append/end/erase/clear, and counts what the renderer half of
 * the RA-D4 health picture reports (stale events, unplaceable strokes).
 */
export class StrokeStore {
  private readonly strokes = new Map<string, LiveStroke>();
  private readonly ttlMs: number;
  private _stale = 0;
  private _unplaced = 0;

  constructor(ttlSeconds = StrokeBounds.defaultTtlSeconds) {
    this.ttlMs = ttlSeconds * 1000;
  }

  get live(): readonly LiveStroke[] {
    return [...this.strokes.values()];
  }

  get counters(): StrokeRenderCounters {
    return { live: this.strokes.size, unplaced: this._unplaced, stale: this._stale };
  }

  /** Apply one validated arrival. Returns the ids whose geometry must be (re)built or removed. */
  apply(arrival: StrokeArrival, nowMs: number): { changed: string[]; removed: string[] } {
    const m = arrival.message;
    const changed: string[] = [];
    const removed: string[] = [];

    switch (m.phase) {
      case 'begin': {
        // Re-begin of a live id replaces it (a lost end); otherwise a new stroke.
        const stroke: LiveStroke = {
          id: m.strokeId,
          sender: arrival.senderIdentity,
          color: m.color,
          width: m.width,
          points: [...m.points],
          poseArrivalMs: arrival.poseArrivalMs,
          committed: false,
          expiresAtMs: null,
          seq: 0,
          dirty: true,
        };
        if (stroke.poseArrivalMs === null) this._unplaced += 1;
        this.strokes.set(m.strokeId, stroke);
        changed.push(m.strokeId);
        removed.push(...this.evictBeyondCap(m.strokeId));
        break;
      }
      case 'append': {
        const stroke = this.strokes.get(m.strokeId);
        if (!stroke || stroke.committed) {
          // An append outrunning its stroke's TTL, or for an erased id: the
          // wearer saw a fragment.
          this._stale += 1;
          break;
        }
        if (stroke.points.length + m.points.length <= StrokeBounds.maxPointsPerStroke) {
          stroke.points.push(...m.points);
        } else {
          stroke.points.push(...m.points.slice(0, Math.max(0, StrokeBounds.maxPointsPerStroke - stroke.points.length)));
        }
        stroke.seq = m.seq;
        stroke.dirty = true;
        changed.push(m.strokeId);
        break;
      }
      case 'end': {
        let stroke = this.strokes.get(m.strokeId);
        if (!stroke) {
          // A lost begin self-heals from end's complete point list.
          stroke = {
            id: m.strokeId,
            sender: arrival.senderIdentity,
            color: m.color,
            width: m.width,
            points: [],
            poseArrivalMs: null,
            committed: false,
            expiresAtMs: null,
            seq: 0,
            dirty: true,
          };
          this.strokes.set(m.strokeId, stroke);
          removed.push(...this.evictBeyondCap(m.strokeId));
        }
        if (m.points.length > 0) stroke.points = [...m.points];
        if (arrival.poseArrivalMs !== null && stroke.poseArrivalMs === null) stroke.poseArrivalMs = arrival.poseArrivalMs;
        stroke.committed = true;
        stroke.expiresAtMs = nowMs + this.ttlMs;
        stroke.dirty = true;
        changed.push(m.strokeId);
        break;
      }
      case 'erase':
        if (this.strokes.delete(m.strokeId)) removed.push(m.strokeId);
        else this._stale += 1;
        break;
      case 'clear': {
        for (const [id, stroke] of this.strokes) {
          if (m.scope === 'all' || stroke.sender === arrival.senderIdentity) {
            this.strokes.delete(id);
            removed.push(id);
          }
        }
        break;
      }
    }

    return { changed, removed };
  }

  /** Expire committed strokes past their TTL. Returns the removed ids. */
  expire(nowMs: number): string[] {
    const removed: string[] = [];
    for (const [id, stroke] of this.strokes) {
      if (stroke.committed && stroke.expiresAtMs !== null && nowMs >= stroke.expiresAtMs) {
        this.strokes.delete(id);
        removed.push(id);
      }
    }
    return removed;
  }

  clear(): string[] {
    const ids = [...this.strokes.keys()];
    this.strokes.clear();
    return ids;
  }

  /** Oldest evicted beyond the live cap - never the stroke that just arrived. */
  private evictBeyondCap(keep: string): string[] {
    const removed: string[] = [];
    while (this.strokes.size > StrokeBounds.maxLiveStrokes) {
      const oldest = [...this.strokes.keys()].find((id) => id !== keep);
      if (!oldest) break;
      this.strokes.delete(oldest);
      removed.push(oldest);
    }
    return removed;
  }
}
